import { addMonths, format, getDaysInMonth } from "date-fns";
import { TFunction } from "i18next";

import { PublicHolidayResponse } from "@/api";
import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";

import {
  AppointmentTargetCalculatorFormValues,
  AppointmentTargetResult,
} from "./AppointmentTargetCalculatorDialog.types";

export const TRANSLATION_BASE = "calculators.appointmentTargets";

/** Full-time weekly hours (Vollzeit-Basis) the hours factor (Stundenfaktor) is measured against. */
export const FULL_TIME_WEEKLY_HOURS = 40;

/** Default weekly targets (Vorgaben) per advisor (overridable in the form). */
export const DEFAULT_1B_TARGET_PER_WEEK = 7;
export const DEFAULT_EG_TARGET_PER_WEEK = 3;

/** Sentinel month option meaning "type the calendar working days by hand". */
export const MANUAL_MONTH_ID = "manual";

/**
 * True when the calendar working days are entered by hand rather than taken from a calendar month:
 * the manual sentinel is selected, or the month has been cleared. A real month supplies its
 * pre-computed working days to the calculation directly.
 */
export function usesManualCalendarWorkdays(month: string | null): boolean {
  return month === MANUAL_MONTH_ID || month == null;
}

/**
 * A full-time week is 5 working days: the 5-day model derives its working days from the calendar
 * (weekdays minus absences), while the part-time models (1–4) take the advisor's actual working days directly.
 */
export const FULL_TIME_DAYS_PER_WEEK = 5;

/**
 * Working days in a calendar month: weekdays (Mon–Fri) that are not public holidays.
 * Every imported `public_holidays` row counts as non-working regardless of its kind — both genuine
 * holidays and the org's Dienstfrei days (24.12 / 31.12) — matching the source Excel's 2026 figures.
 */
function countWorkingDaysInMonth(year: number, monthIndex: number, holidays: PublicHolidayResponse[]): number {
  const holidayDays = new Set<number>();
  for (const holiday of holidays) {
    if (holiday.date.getUTCFullYear() === year && holiday.date.getUTCMonth() === monthIndex) {
      holidayDays.add(holiday.date.getUTCDate());
    }
  }

  const daysInMonth = getDaysInMonth(new Date(year, monthIndex, 1));
  let workingDays = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const weekday = new Date(year, monthIndex, day).getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    if (!isWeekend && !holidayDays.has(day)) {
      workingDays++;
    }
  }
  return workingDays;
}

export interface MonthData {
  /** Options for the month dropdown, including the "manual entry" sentinel. */
  options: IOptionItem[];
  /** Calendar working days keyed by option id (real months only — the sentinel is absent). */
  workdaysById: Map<string, number>;
}

// Targets are planned mostly for upcoming months and only rarely looked back on, hence the lopsided range.
const MONTHS_BEFORE_CURRENT = 3;
const MONTHS_AFTER_CURRENT = 6;

// The range crosses the turn of the year, so a month index alone would not identify an option.
const MONTH_ID_FORMAT = "yyyy-MM";

/**
 * Builds the month dropdown around the current month: a "manual entry" sentinel plus one entry per month
 * of the rolling range, each carrying that month's calendar working days for prefilling the field. Month
 * names are formatted in the active app language (locale data, like the separators in formatNumber.ts).
 */
export function buildMonthData(
  t: TFunction,
  locale: string,
  currentMonth: Date,
  holidays: PublicHolidayResponse[]
): MonthData {
  const monthNameFormatter = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
  const options: IOptionItem[] = [{ id: MANUAL_MONTH_ID, label: t(`${TRANSLATION_BASE}.fields.monthManual`) }];
  const workdaysById = new Map<string, number>();

  for (let offset = -MONTHS_BEFORE_CURRENT; offset <= MONTHS_AFTER_CURRENT; offset++) {
    const month = addMonths(currentMonth, offset);
    const id = format(month, MONTH_ID_FORMAT);
    const workdays = countWorkingDaysInMonth(month.getFullYear(), month.getMonth(), holidays);
    const name = monthNameFormatter.format(month);
    workdaysById.set(id, workdays);
    options.push({ id, label: t(`${TRANSLATION_BASE}.fields.monthOption`, { month: name, days: workdays }) });
  }

  return { options, workdaysById };
}

/**
 * Live 1B/EG target calculation. Returns null until the inputs needed for the selected model are present.
 * Mirrors the source workbook: weeks = effective workdays / days-per-week, scaled by hours/40.
 */
export function calculateTargets(values: AppointmentTargetCalculatorFormValues): AppointmentTargetResult | null {
  const weeklyHours = values.weeklyHours;
  if (weeklyHours == null || weeklyHours <= 0) {
    return null;
  }

  let effectiveWorkdays: number;
  if (values.workdays === FULL_TIME_DAYS_PER_WEEK) {
    if (values.calendarWorkdays == null) {
      return null;
    }
    effectiveWorkdays =
      values.calendarWorkdays -
      (values.vacationDays ?? 0) -
      (values.trainingDays ?? 0) -
      (values.otherAbsenceDays ?? 0);
  } else {
    if (values.actualWorkdays == null) {
      return null;
    }
    effectiveWorkdays = values.actualWorkdays;
  }

  // Absences can over-subtract; never let the target go negative.
  effectiveWorkdays = Math.max(0, effectiveWorkdays);

  const target1bPerWeek = values.target1bPerWeek ?? 0;
  const targetEgPerWeek = values.targetEgPerWeek ?? 0;
  const weeksPerMonth = effectiveWorkdays / values.workdays;
  const hoursFactor = weeklyHours / FULL_TIME_WEEKLY_HOURS;
  const exact1b = weeksPerMonth * target1bPerWeek * hoursFactor;
  const exactEg = weeksPerMonth * targetEgPerWeek * hoursFactor;

  return {
    effectiveWorkdays,
    workdaysPerWeek: values.workdays,
    weeksPerMonth,
    hoursFactor,
    weeklyHours,
    target1bPerWeek,
    targetEgPerWeek,
    exact1b,
    exactEg,
    target1b: Math.round(exact1b),
    targetEg: Math.round(exactEg),
  };
}
